import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import '../styles/new-article.css';
import api from '../api';
import Icon from './Icon';
import { Spinner } from './ui';

const COUNTDOWN_SECS = 3;

function formatTime(secs) {
  const m = String(Math.floor(secs / 60)).padStart(2, '0');
  const s = String(secs % 60).padStart(2, '0');
  return `${m}:${s}`;
}

export default function Recorder({ onRecordingReady }) {
  const micId = useId();
  // Device selection
  const [mics, setMics] = useState([]);
  const [selectedMic, setSelectedMic] = useState(() => localStorage.getItem('gp_last_mic') || '');

  // State machine: idle | countdown | recording | saving | error
  const [phase, setPhase] = useState('idle');
  const [countdown, setCountdown] = useState(COUNTDOWN_SECS);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savedPath, setSavedPath] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  // Load available mics on mount
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        setMics(audioInputs);
        // If saved mic no longer listed, clear it
        if (selectedMic && !audioInputs.find(d => d.deviceId === selectedMic)) {
          setSelectedMic('');
        }
      })
      .catch(() => setMics([]));
  }, []);

  const stopAllStreams = useCallback(() => {
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    micStreamRef.current = null;
    clearInterval(timerRef.current);
    clearInterval(countdownRef.current);
  }, []);

  // Clean up on unmount
  useEffect(() => () => stopAllStreams(), [stopAllStreams]);

  const startCountdown = useCallback(async () => {
    setErrorMsg('');
    setPhase('countdown');
    setCountdown(COUNTDOWN_SECS);

    // Request screen now so picker appears before countdown
    let screenStream;
    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30, cursor: 'always' },
        audio: false,
      });
    } catch (e) {
      setPhase('idle');
      if (e.name !== 'NotAllowedError') setErrorMsg('Screen permission denied or cancelled.');
      return;
    }
    screenStreamRef.current = screenStream;

    // Request mic
    let micStream = null;
    if (selectedMic || mics.length > 0) {
      try {
        const constraints = { audio: selectedMic ? { deviceId: { exact: selectedMic } } : true };
        micStream = await navigator.mediaDevices.getUserMedia(constraints);
        micStreamRef.current = micStream;
      } catch {
        // Mic failed — proceed without audio, user will be warned
      }
    }

    // Countdown tick
    let remaining = COUNTDOWN_SECS;
    setCountdown(remaining);
    countdownRef.current = setInterval(() => {
      remaining--;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        beginRecording(screenStream, micStream);
      }
    }, 1000);
  }, [selectedMic, mics]);

  const beginRecording = useCallback((screenStream, micStream) => {
    chunksRef.current = [];

    const tracks = [...screenStream.getVideoTracks()];
    if (micStream) tracks.push(...micStream.getAudioTracks());
    const combined = new MediaStream(tracks);

    // Pick best supported MIME
    const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      .find(m => MediaRecorder.isTypeSupported(m)) || '';

    const recorder = new MediaRecorder(combined, mimeType ? { mimeType } : {});
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => saveRecording(mimeType);

    // Stop recording if user closes the browser's share dialog
    screenStream.getVideoTracks()[0].onended = () => {
      if (mediaRecorderRef.current?.state === 'recording') stopRecording();
    };

    recorder.start(1000); // collect chunks every second
    setPhase('recording');
    setElapsed(0);
    setPaused(false);

    timerRef.current = setInterval(() => {
      setElapsed(s => s + 1);
    }, 1000);
  }, []);

  const stopRecording = useCallback(() => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    stopAllStreams();
    setPhase('saving');
  }, [stopAllStreams]);

  const togglePause = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    if (paused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
      setPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setPaused(true);
    }
  }, [paused]);

  const saveRecording = useCallback(async (mimeType) => {
    const contentType = mimeType || 'video/webm';
    const blob = new Blob(chunksRef.current, { type: contentType });

    if (blob.size === 0) {
      setErrorMsg('Recording was empty — nothing was captured.');
      setPhase('error');
      return;
    }

    try {
      // Ask backend for a presigned R2 PUT URL — upload goes browser → R2 directly,
      // Railway is not in the data path at all so there's no timeout or size limit.
      const { data } = await api.post('/recording-upload-url', { content_type: contentType });
      const { upload_url, file_url } = data;

      const res = await fetch(upload_url, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': contentType },
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);

      setSavedPath(file_url);
      setPhase('done');
    } catch (e) {
      // The direct-to-R2 path failed. This is the fragile path that caused the
      // 2026-08-05 data loss — log it loudly so a silent CORS regression on the
      // R2 bucket is visible instead of quietly degrading to the Railway upload.
      console.error(
        '[affixly][recording] Direct R2 upload FAILED — falling back to the Railway ' +
        'save path. If this happens for everyone, the R2 bucket CORS has likely ' +
        'regressed (check the backend "[R2] CORS NOT configured" boot warning).',
        e
      );
      // Fallback: send through Railway if R2 isn't configured or presigned URL fails
      try {
        const ext = contentType.includes('mp4') ? '.mp4' : '.webm';
        const formData = new FormData();
        formData.append('file', blob, `recording${ext}`);
        const res = await api.post('/save-recording', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
        });
        if (res.data.persisted === false) {
          console.warn(
            '[affixly][recording] Saved via Railway but the server could NOT persist ' +
            'the recording to cloud storage — this article is at risk until re-processed.'
          );
        }
        setSavedPath(res.data.file_path);
        setPhase('done');
      } catch (e2) {
        setErrorMsg(e2.response?.data?.error || 'Failed to save recording. Try a shorter recording or use the Upload MP4 tab.');
        setPhase('error');
      }
    }
  }, []);

  const handleMicChange = (deviceId) => {
    setSelectedMic(deviceId);
    localStorage.setItem('gp_last_mic', deviceId);
  };

  const reset = () => {
    stopAllStreams();
    setPhase('idle');
    setElapsed(0);
    setPaused(false);
    setErrorMsg('');
    setSavedPath('');
    chunksRef.current = [];
  };

  // ── Render phases ──────────────────────────────────────────────────────────
  // Styles: styles/new-article.css (na-rec*). This component is also mounted
  // by UpdateArticle, ReportIssue and TestRunModal — keep it self-contained.

  if (phase === 'done') {
    return (
      <div className="na-rec">
        <div className="inline-msg is-success" role="status">
          <Icon name="checkCircle" />
          <div className="na-msg-body">
            <p>Recording saved. Ready to process.</p>
            <p className="na-mono" style={{ color: 'var(--gp-text-secondary)' }}>{savedPath.split('/').pop()}</p>
          </div>
        </div>
        <div className="na-row">
          <button type="button" className="btn btn-primary" onClick={() => onRecordingReady(savedPath)}>
            Generate Documentation <Icon name="chevronRight" />
          </button>
          <button type="button" className="btn btn-secondary" onClick={reset}>
            <Icon name="refresh" /> Record again
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="na-rec">
        <div className="inline-msg is-error" role="alert">
          <Icon name="alert" />
          <div className="na-msg-body">{errorMsg}</div>
        </div>
        <div>
          <button type="button" className="btn btn-secondary" onClick={reset}>
            <Icon name="refresh" /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'saving') {
    return (
      <div className="na-rec-saving" role="status">
        <Spinner size={20} />
        Saving recording…
      </div>
    );
  }

  if (phase === 'countdown') {
    return (
      <div className="na-rec-count" role="status" aria-live="assertive">
        <div className="na-rec-count-num" aria-hidden={countdown <= 0 || undefined}>
          {countdown > 0 ? countdown : <Icon name="record" size={56} />}
        </div>
        <div className="na-text">
          {countdown > 0 ? 'Recording starts in…' : 'Recording started'}
        </div>
      </div>
    );
  }

  if (phase === 'recording') {
    return (
      <div className="na-rec">
        {/* Status bar */}
        <div className="na-rec-bar">
          <span className={`na-rec-dot ${paused ? 'is-paused' : ''}`} aria-hidden="true" />
          <span className="na-rec-state" role="status">
            {paused ? 'Paused' : 'Recording'}
          </span>
          <span className="na-rec-time" aria-label={`Elapsed ${formatTime(elapsed)}`}>
            {formatTime(elapsed)}
          </span>
        </div>

        {/* Controls */}
        <div className="na-row">
          <button type="button" className="btn btn-danger" onClick={stopRecording}>
            <Icon name="stop" /> Stop recording
          </button>
          <button type="button" className="btn btn-secondary" onClick={togglePause}>
            {paused ? <><Icon name="play" /> Resume</> : 'Pause'}
          </button>
        </div>

        <p className="field-hint" style={{ marginTop: 0 }}>
          Switch back to this window and choose Stop recording when you’re done.
        </p>
        {elapsed > 600 && (
          <div className="inline-msg is-warning">
            <Icon name="warning" />
            <div className="na-msg-body">Long recording — transcription will take a few extra minutes after you stop.</div>
          </div>
        )}
      </div>
    );
  }

  // ── Idle: setup UI ─────────────────────────────────────────────────────────
  return (
    <div className="na-rec">
      {errorMsg && (
        <div className="inline-msg is-error" role="alert">
          <Icon name="alert" />
          <div className="na-msg-body">{errorMsg}</div>
        </div>
      )}

      {/* Microphone picker */}
      <div className="form-group">
        {mics.length === 0 ? (
          <>
            <div className="label">Microphone</div>
            <p className="na-text">No microphones found. Recording will capture screen only.</p>
          </>
        ) : (
          <>
          <label className="label" htmlFor={micId}>Microphone</label>
          <select
            id={micId}
            className="select"
            value={selectedMic}
            onChange={e => handleMicChange(e.target.value)}
          >
            <option value="">System default</option>
            {mics.map(m => (
              <option key={m.deviceId} value={m.deviceId}>
                {m.label || `Microphone ${m.deviceId.slice(0, 8)}`}
              </option>
            ))}
          </select>
          </>
        )}
      </div>

      {/* Info */}
      <p className="na-text">
        Choose <strong>Start recording</strong> — your browser will ask you to
        pick a screen or window to share and for microphone access. Recording begins after a {COUNTDOWN_SECS}-second countdown.
      </p>

      <div>
        <button type="button" className="btn btn-primary" onClick={startCountdown}>
          <Icon name="record" /> Start recording
        </button>
      </div>
    </div>
  );
}
